import { useEffect, useRef } from 'react';
import type { MutableRefObject } from 'react';

import { bind } from 'bind-event-listener';

import { findOverflowScrollParent } from '@atlaskit/editor-common/Popup/utils';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/useSharedPluginStateWithSelector';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import type { AIPlugin } from '../../../../aiPluginType';
import { setHasUserScrolled } from '../../../../pm-plugins/ai-session-context/commands';
import { calculateBottomScroll, calculateTopScroll } from '../utils/reviewScroll';

/**
 * Manages auto-scroll behavior during AI content streaming.
 *
 * This hook automatically scrolls the viewport to keep streamed content visible
 * while respecting user scroll intentions. When the user manually scrolls during
 * streaming, auto-scroll is disabled for the remainder of that streaming session.
 *
 * ## Behavior:
 * - **Initial scroll**: When streaming starts, scrolls to show the start position
 * - **Continuous scroll**: As content streams in, keeps new content visible at the bottom
 * - **User override**: Detects manual user scrolls and disables auto-scroll
 * - **Modal awareness**: Accounts for AI modal height to prevent content obstruction
 *
 * ## Scroll Detection:
 * Uses a flag-based system to distinguish programmatic scrolls from user scrolls:
 * - Before programmatic scroll: Sets `isProgrammaticScrollRef` to `true`
 * - On any scroll event: Checks if flag is `true` (programmatic) or `false` (user)
 * - After any scroll event: Resets flag to `false`
 *
 * @param options - Hook options
 * @param options.api - The editor plugin API for accessing streaming state
 * @param options.view - The ProseMirror EditorView instance
 * @param options.modalRef - Optional ref to the AI modal for height calculations
 */
export const useStreamingScroll = ({
	api,
	view,
	modalRef,
}: {
	api: ExtractInjectionAPI<AIPlugin> | undefined;
	modalRef?: React.RefObject<HTMLDivElement>;
	view: EditorView | undefined;
}): MutableRefObject<boolean> => {
	const scrollParentRef = useRef<HTMLElement | null>(null);
	const isProgrammaticScrollRef = useRef(false);

	const { streamStartPos, scrollTargetPos, isStreaming, hasUserScrolled } =
		useSharedPluginStateWithSelector(api, ['aiExperience'], (state) => ({
			hasUserScrolled: state.aiExperienceState?.aiSessionPluginState?.hasUserScrolled,
			streamStartPos: state.aiExperienceState?.streamingState?.streamStartPos,
			scrollTargetPos: state.aiExperienceState?.streamingState?.scrollTargetPos,
			isStreaming: state.aiExperienceState?.isStreaming ?? false,
		}));

	useEffect(() => {
		if (!view || !isStreaming || hasUserScrolled) {
			return;
		}

		// Find and cache the scroll container
		const scrollParent = findOverflowScrollParent(view.dom);
		if (scrollParent) {
			scrollParentRef.current = scrollParent;
		}

		// Only attach scroll listener during active streaming
		if (!scrollParent) {
			return;
		}

		/**
		 * Detects user scrolls by checking if the scroll was programmatic.
		 * - If isProgrammaticScrollRef is true: This scroll was triggered by our code → ignore it
		 * - If isProgrammaticScrollRef is false: This is a user scroll → disable auto-scroll
		 * - Always reset the flag after processing so the next scroll is treated as a user scroll
		 */

		const unbind = bind(scrollParent, {
			type: 'scroll',
			listener: handleScroll,
			options: { passive: true },
		});

		function handleScroll() {
			if (!isProgrammaticScrollRef.current) {
				api?.core?.actions.execute(setHasUserScrolled());
				// Early exit once user scroll has been registered.
				unbind();
			}
			isProgrammaticScrollRef.current = false;
		}

		return unbind;
	}, [view, isStreaming, hasUserScrolled, api]);

	useEffect(() => {
		const scrollParent = scrollParentRef.current;

		// Bail out early if conditions aren't met for auto-scroll
		if (hasUserScrolled || !scrollParent || !view || streamStartPos === undefined) {
			return;
		}

		// Attempt to perform auto-scroll
		// Wrapped in try-catch because positions may be temporarily invalid during document changes
		try {
			const modalHeight = modalRef?.current?.offsetHeight || 0;

			// Case 1: Only start position is available → ensure start is visible at top
			if (scrollTargetPos === undefined) {
				const topScrollTop = calculateTopScroll({ view, pos: streamStartPos, scrollParent });

				// Only scroll up if needed (don't scroll down, user might be reading above)
				if (scrollParent.scrollTop > topScrollTop) {
					isProgrammaticScrollRef.current = true;
					scrollParent.scroll({ top: topScrollTop, behavior: 'instant' });
				}
				return;
			}

			// Case 2: Both start and target positions available → keep target visible at bottom
			const bottomScrollTop = calculateBottomScroll({
				view,
				pos: scrollTargetPos,
				scrollParent,
				modalHeight,
			});

			// Only scroll down if needed (target is below viewport)
			if (scrollParent.scrollTop < bottomScrollTop) {
				isProgrammaticScrollRef.current = true;
				scrollParent.scroll({ top: bottomScrollTop, behavior: 'instant' });
			}
		} catch {
			// Silently ignore errors - positions may be invalid during rapid document mutations
		}
	}, [streamStartPos, scrollTargetPos, isStreaming, view, modalRef, hasUserScrolled]);

	return isProgrammaticScrollRef;
};
